import { equalJSON } from '@/utils'

export type Metadata = Record<string, unknown>

// A loaded value that is not a string or number is kept as is: rows only edit flat text.
export interface MetadataRow {
  id: string
  key: string
  value: string
  loadedKey?: string
  loadedValue?: unknown
}

export type MetadataRowProblem =
  | 'key_required'
  | 'key_format'
  | 'key_reserved'
  | 'key_duplicate'
  | 'value_required'

export const EXTERNAL_ID_METADATA_KEY = 'external_id'

// Mirrors settings-api's MaxBytesInMetadata so oversized metadata fails inline instead of on save.
export const MAX_METADATA_BYTES = 16 * 1024

const KEY_FORMAT = /^[a-z][a-z0-9_]*$/

const isText = (value: unknown): value is string | number =>
  typeof value === 'string' || typeof value === 'number'

export const isLockedRow = (row: MetadataRow) =>
  row.loadedValue !== undefined && !isText(row.loadedValue)

// An untouched stored key is kept exactly, even with spaces a new key would have trimmed.
const rowKey = (row: MetadataRow) =>
  row.key === row.loadedKey ? row.key : row.key.trim()

// A row left with no key and no value is skipped: it neither saves nor blocks the save.
const isBlankRow = (row: MetadataRow) =>
  row.loadedKey === undefined && row.key.trim() === '' && row.value === ''

export const BLANK_METADATA_ROW: MetadataRow = {
  id: 'new-blank',
  key: '',
  value: ''
}

// Keeps one empty row on screen, so there is always a pair ready to fill.
export const withBlankRow = (rows: MetadataRow[]): MetadataRow[] =>
  rows.length ? rows : [BLANK_METADATA_ROW]

export const toMetadataRows = (metadata: Metadata): MetadataRow[] =>
  Object.entries(metadata)
    .filter(([key]) => key !== EXTERNAL_ID_METADATA_KEY)
    .map(([key, value], i) => ({
      id: `saved-${i}`,
      key,
      value: isText(value) ? String(value) : JSON.stringify(value),
      loadedKey: key,
      loadedValue: value
    }))

// Undefined when the stored value is not plain text, so the field cannot edit it.
export const externalIdText = (metadata: Metadata): string | undefined => {
  const value = metadata[EXTERNAL_ID_METADATA_KEY]
  if (value === undefined) return ''
  return isText(value) ? String(value) : undefined
}

export const buildMetadata = (
  loaded: Metadata,
  externalId: string,
  rows: MetadataRow[]
): Metadata => {
  const metadata: Metadata = {}
  const loadedExternalId = loaded[EXTERNAL_ID_METADATA_KEY]
  const loadedText = externalIdText(loaded)
  // Unedited, the stored value is kept as is, including an empty string or a non-text value.
  if (loadedText === undefined || externalId === loadedText) {
    if (loadedExternalId !== undefined) {
      metadata[EXTERNAL_ID_METADATA_KEY] = loadedExternalId
    }
  } else if (externalId !== '') {
    metadata[EXTERNAL_ID_METADATA_KEY] = toValue(externalId, loadedExternalId)
  }
  for (const row of rows.filter((row) => !isBlankRow(row))) {
    metadata[rowKey(row)] = isLockedRow(row)
      ? row.loadedValue
      : toValue(row.value, row.loadedValue)
  }
  return metadata
}

// Stored keys and values are only checked once edited, so data written elsewhere never blocks a save.
export const metadataRowProblems = (
  rows: MetadataRow[]
): Record<string, MetadataRowProblem> => {
  const filled = rows.filter((row) => !isBlankRow(row))
  const uses = new Map<string, number>()
  for (const row of filled) {
    const key = rowKey(row)
    uses.set(key, (uses.get(key) ?? 0) + 1)
  }
  const problems: Record<string, MetadataRowProblem> = {}
  for (const row of filled) {
    const problem = rowProblem(row, (uses.get(rowKey(row)) ?? 0) > 1)
    if (problem) problems[row.id] = problem
  }
  return problems
}

// settings-api measures with Go's encoding/json, which also escapes these characters as \uXXXX.
export const isMetadataTooLarge = (metadata: Metadata) => {
  const serialized = JSON.stringify(metadata).replace(
    /[<>&\u2028\u2029]/g,
    (char) => `\\u${char.charCodeAt(0).toString(16).padStart(4, '0')}`
  )
  return new TextEncoder().encode(serialized).length > MAX_METADATA_BYTES
}

export const sameMetadata = (a: Metadata, b: Metadata) =>
  Object.keys(a).length === Object.keys(b).length &&
  Object.keys(a).every((key) => key in b && equalJSON(a[key], b[key]))

const rowProblem = (
  row: MetadataRow,
  duplicated: boolean
): MetadataRowProblem | undefined => {
  const key = rowKey(row)
  const keyEdited = key !== row.loadedKey
  if (keyEdited && !key) return 'key_required'
  if (key === EXTERNAL_ID_METADATA_KEY) return 'key_reserved'
  if (keyEdited && !KEY_FORMAT.test(key)) return 'key_format'
  if (duplicated) return 'key_duplicate'
  const valueEdited = row.value !== row.loadedValue
  if (!isLockedRow(row) && valueEdited && row.value === '') {
    return 'value_required'
  }
  return undefined
}

// The loaded type wins, so a stored number stays a number while its text still reads as one.
const toValue = (text: string, loaded: unknown) =>
  typeof loaded === 'number' && isNumberText(text) ? Number(text) : text

// True only for text that reads back identically as a safe number, so "007" or "1e3" stay strings.
const isNumberText = (value: string) => {
  const number = Number(value)
  return Math.abs(number) <= Number.MAX_SAFE_INTEGER && String(number) === value
}
